import { useState } from 'react'
import { useApp } from '../store/AppContext'
import {
  loadRoutines,
  saveRoutines,
  loadShopping,
  saveShopping,
  type Routine,
  type ShoppingItem,
} from '../lib/dailyLiving'

export function ActionableTasksWidget({ compact = false }: { compact?: boolean }) {
  const { currentUser, tasks, toggleTaskStatus } = useApp()
  const visibleTasks = tasks.filter((task) =>
    task.status === 'todo' &&
    (task.assignedToUserId === currentUser.id || ['participant', 'rep', 'coordinator'].includes(currentUser.role)),
  )

  if (!visibleTasks.length) {
    return <p className="mt-2 text-xs text-slate-500">No open tasks.</p>
  }

  return (
    <div className="mt-3 flex flex-col gap-2">
      {visibleTasks.slice(0, compact ? 3 : 5).map((task) => (
        <button
          key={task.id}
          type="button"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            toggleTaskStatus(task.id)
          }}
          className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-left"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-brand-300 bg-white text-sm">○</span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold text-slate-800">{task.title}</span>
            {task.dueDate && <span className="block text-[11px] text-slate-500">Due {task.dueDate}</span>}
          </span>
          <span className="text-[11px] font-bold text-brand-600">Done</span>
        </button>
      ))}
      {visibleTasks.length > (compact ? 3 : 5) && (
        <p className="text-[11px] font-semibold text-slate-400">+ {visibleTasks.length - (compact ? 3 : 5)} more</p>
      )}
    </div>
  )
}

export function ActionableRoutinesWidget({ compact = false }: { compact?: boolean }) {
  const { currentUser } = useApp()
  const [routines, setRoutines] = useState<Routine[]>(loadRoutines)
  const active = routines.filter((routine) => routine.steps.some((step) => !step.done))

  const toggleStep = (routineId: string, stepId: string) => {
    const next = routines.map((routine) =>
      routine.id !== routineId
        ? routine
        : {
            ...routine,
            steps: routine.steps.map((step) =>
              step.id !== stepId
                ? step
                : {
                    ...step,
                    done: !step.done,
                    completedByUserId: !step.done ? currentUser.id : undefined,
                    completedAt: !step.done ? new Date().toISOString() : undefined,
                  },
            ),
          },
    )
    setRoutines(next)
    saveRoutines(next)
  }

  if (!active.length) {
    return <p className="mt-2 text-xs text-slate-500">No routine steps waiting.</p>
  }

  const routine = active[0]
  const openSteps = routine.steps.filter((step) => !step.done)

  return (
    <div className="mt-3">
      <p className="text-xs font-bold text-slate-700">{routine.title}</p>
      <div className="mt-2 flex flex-col gap-2">
        {openSteps.slice(0, compact ? 3 : 5).map((step) => (
          <button
            key={step.id}
            type="button"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              toggleStep(routine.id, step.id)
            }}
            className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-left"
          >
            <span className="text-xl">{step.icon || '○'}</span>
            <span className="flex-1 text-sm font-bold text-slate-800">{step.text}</span>
            <span className="text-[11px] font-bold text-brand-600">Done</span>
          </button>
        ))}
      </div>
    </div>
  )
}

export function ActionableShoppingWidget({ compact = false }: { compact?: boolean }) {
  const { currentUser } = useApp()
  const [items, setItems] = useState<ShoppingItem[]>(loadShopping)
  const openItems = items.filter((item) => !item.done)

  const toggleItem = (itemId: string) => {
    const next = items.map((item) =>
      item.id === itemId
        ? {
            ...item,
            done: !item.done,
            completedByUserId: !item.done ? currentUser.id : undefined,
            completedAt: !item.done ? new Date().toISOString() : undefined,
          }
        : item,
    )
    setItems(next)
    saveShopping(next)
  }

  if (!openItems.length) {
    return <p className="mt-2 text-xs text-slate-500">Nothing on the shopping list.</p>
  }

  return (
    <div className="mt-3 flex flex-col gap-2">
      {openItems.slice(0, compact ? 4 : 6).map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            toggleItem(item.id)
          }}
          className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-left"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-teal-300 bg-white text-sm">○</span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold text-slate-800">{item.text}</span>
            <span className="block text-[11px] text-slate-500">{item.category}</span>
          </span>
          <span className="text-[11px] font-bold text-teal-700">Got it</span>
        </button>
      ))}
      {openItems.length > (compact ? 4 : 6) && (
        <p className="text-[11px] font-semibold text-slate-400">+ {openItems.length - (compact ? 4 : 6)} more</p>
      )}
    </div>
  )
}

